import React, { Suspense } from 'react';
import { useSelector } from 'react-redux';
import { Divider, Flex, Form, Inspector } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { AudioElement, AudioElementParams, type MultiPageVideoModel } from '@bynder-studio/render-core';
import { GainSlider } from 'packages/pages/editor/RightSideMenu/FormComponents/AudioFeature/GainSlider';
import { AudioSliderInput } from 'packages/pages/editor/RightSideMenu/FormComponents/AudioFeature/AudioSliderInput';
import { FadeInput } from 'packages/pages/editor/RightSideMenu/FormComponents/AudioFeature/FadeInput';
import useEditorSelectedElement from '~/hooks/useEditorSelectedElement';
import useEditor from '~/hooks/useEditor';
import { FormSectionContent, InspectorContent, Section } from 'packages/pages/editor/RightSideMenu/index.styled';
import Duration from '../Duration';
import AssetSource from 'packages/pages/editor/RightSideMenu/Shots/AssetSource';
import PersonalUploads from 'packages/pages/editor/RightSideMenu/Shots/PersonalUploads';
import useAccessRights from 'packages/hooks/useAccessRights';
import AllowToggleVisibility from 'packages/pages/editor/RightSideMenu/Shots/AllowToggleVisibility';
import useAudio from 'packages/hooks/useAudio';
import ContentProperty from 'packages/pages/editor/RightSideMenu/Shots/ContentProperty';
import { showAssetSelectionSelector } from 'packages/store/creativeEditor/creativeEditor.selectors';
import BrowseButton from 'packages/pages/editor/RightSideMenu/FormComponents/BrowseButton';
import { ContentType } from 'packages/pages/editor/RightSideMenu/FormComponents/BrowseButton/types';
import { setShowAssetSelection } from 'packages/store/creativeEditor/creativeEditor.actions';
import { getElementPropertiesFromSource } from '~/common/editor/helpers';
import features from '~/configs/features';
import { InputWrapper, SliderWrapper } from './index.styled';

const MIN_GAIN_VOLUME = -60;
const MAX_GAIN_VOLUME = 20;

const Audio = () => {
    const { translate } = useTranslate();
    const { isPlatformAllowed } = useAccessRights();
    const showAssetSelection = useSelector(showAssetSelectionSelector);
    const { selectedElement } = useEditorSelectedElement<AudioElement>();
    const { creativeModel, frameRate } = useEditor<MultiPageVideoModel>();

    const { duration, fileName } = selectedElement;

    const onAudioChange = (params: AudioElementParams) => {
        creativeModel.updateElement(selectedElement.id, params);
    };

    const { gain, setGain, setFadeInOut, fadeIn, fadeOut, locked } = useAudio({
        audio: selectedElement,
        onAudioChange,
    });

    const handleSelectSource = (selectedSource: unknown) => {
        const params = getElementPropertiesFromSource(selectedElement, 'AUDIO', selectedSource);

        creativeModel.updateElement(selectedElement.id, params);
    };

    const defaultProps = {
        creativeModel,
        selectedElement,
        disabled: locked,
    };

    return (
        <>
            <Inspector.Header title={translate('editor.sidebar.shots.audio.tabs.text')} />
            <InspectorContent>
                <Divider />
                {isPlatformAllowed([features.CONTENT_PROPERTIES]) && (
                    <Section aria-labelledby="shots-audio-content-property">
                        <Suspense>
                            <ContentProperty {...defaultProps} />
                        </Suspense>
                    </Section>
                )}
                <Divider />
                <Section aria-labelledby="shots-audio-asset-settings">
                    <FormSectionContent onSubmit={preventSubmit}>
                        <Form.Group>
                            {selectedElement.src && (
                                <Form.Label>{translate('editor.sidebar.shots.audio.tabs.content')}</Form.Label>
                            )}
                            <BrowseButton
                                type={ContentType.AUDIO}
                                frameRate={frameRate}
                                fileName={fileName}
                                selectedObject={{ element: selectedElement, duration }}
                                disabled={defaultProps.disabled}
                                handleSelectSource={handleSelectSource}
                                show={showAssetSelection}
                                setShowAssetSelection={setShowAssetSelection}
                                mediaSrc={selectedElement.src}
                                testId="shots_audio_asset"
                                isEditorPage
                                resizableRange
                            />
                        </Form.Group>
                    </FormSectionContent>
                </Section>
                <Divider />
                <Section aria-labelledby="shots-audio-settings">
                    <FormSectionContent onSubmit={preventSubmit}>
                        <Form.Group>
                            <Form.Label id="audio-volume-label" htmlFor="audio-volume">
                                {translate('editor.sidebar.audio.volume.label')}
                            </Form.Label>

                            <Flex alignItems="center" justifyContent="space-between">
                                <SliderWrapper>
                                    <GainSlider
                                        min={MIN_GAIN_VOLUME}
                                        max={MAX_GAIN_VOLUME}
                                        gain={gain}
                                        onGainChange={setGain}
                                        isDisabled={defaultProps.disabled}
                                    />
                                </SliderWrapper>
                                <InputWrapper>
                                    <AudioSliderInput
                                        id="audio-volume-input"
                                        value={gain}
                                        min={MIN_GAIN_VOLUME}
                                        max={MAX_GAIN_VOLUME}
                                        isDisabled={defaultProps.disabled}
                                        onChange={setGain}
                                    />
                                </InputWrapper>
                            </Flex>
                        </Form.Group>
                        <Form.Row>
                            <Form.Group>
                                <Form.Label htmlFor="audio-fade-in">
                                    {translate('editor.sidebar.audio.fade.in.label')}
                                </Form.Label>
                                <FadeInput
                                    id="audio-fade-in"
                                    value={fadeIn}
                                    isDisabled={defaultProps.disabled}
                                    onChange={setFadeInOut('fadeIn')}
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label htmlFor="audio-fade-out">
                                    {translate('editor.sidebar.audio.fade.out.label')}
                                </Form.Label>
                                <FadeInput
                                    id="audio-fade-out"
                                    value={fadeOut}
                                    isDisabled={defaultProps.disabled}
                                    onChange={setFadeInOut('fadeOut')}
                                />
                            </Form.Group>
                        </Form.Row>
                    </FormSectionContent>
                </Section>
                <Divider />
                <Section aria-labelledby="shots-audio-duration">
                    <FormSectionContent>
                        <Duration {...defaultProps} frameRate={frameRate} />
                    </FormSectionContent>
                </Section>
                <Divider />
                <Section aria-labelledby="shots-audio-asset-source">
                    <FormSectionContent>
                        <Form.Group>
                            <AssetSource disabled={defaultProps.disabled} />
                        </Form.Group>
                    </FormSectionContent>
                </Section>
                <Divider />
                {isPlatformAllowed([features.INTERNAL_ASSET]) && (
                    <Section aria-labelledby="shots-audio-duration">
                        <FormSectionContent>
                            <Form.Group>
                                <PersonalUploads disabled={defaultProps.disabled} />
                            </Form.Group>
                        </FormSectionContent>
                    </Section>
                )}
                <Divider />
                <Section aria-labelledby="shots-audio-visibility">
                    <FormSectionContent onSubmit={preventSubmit}>
                        <Form.Group>
                            <Form.Label>{translate('editor.element.audio_control')}</Form.Label>
                            <AllowToggleVisibility {...defaultProps} />
                        </Form.Group>
                    </FormSectionContent>
                </Section>
                <Divider />
            </InspectorContent>
        </>
    );
};

export default Audio;

function preventSubmit(e: React.FormEvent) {
    e.preventDefault();
}
